import { useEffect, useState } from 'react';
import api from '../services/api';

export type OrgCuratedCurrencyState = {
  curatedCodes: string[];
  orgDefaultCurrency: string | null;
  loading: boolean;
  error: string | null;
};

/**
 * Loads organization default + additional currencies for pickers (any org member).
 */
export function useOrgCuratedCurrencies(organizationId: string | undefined): OrgCuratedCurrencyState {
  const [curatedCodes, setCuratedCodes] = useState<string[]>([]);
  const [orgDefaultCurrency, setOrgDefaultCurrency] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!organizationId) {
      setCuratedCodes([]);
      setOrgDefaultCurrency(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = (await api.getOrganizationCurrencyCurated(organizationId)) as {
          success?: boolean;
          data?: { curatedCodes?: string[]; defaultCurrency?: string | null };
        };
        const payload = res?.data;
        const codes = payload?.curatedCodes ?? [];
        const def = payload?.defaultCurrency ?? null;
        if (!cancelled) {
          setCuratedCodes(codes);
          setOrgDefaultCurrency(def);
        }
      } catch (e: unknown) {
        const err = e as { response?: { data?: { message?: string } } };
        if (!cancelled) {
          setError(err.response?.data?.message || 'Failed to load organization currencies');
          setCuratedCodes([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  return { curatedCodes, orgDefaultCurrency, loading, error };
}
